import { EntityType, EntityTypeMap } from '@redviking/argonaut-core-ui/types';
import { getterTree, mutationTree } from 'typed-vuex';
import _cloneDeep from 'lodash.clonedeep';
import type { PendingMediaObject } from '@redviking/argonaut-core-ui/src/media/media.util';

export type StoredEntityMap = {
    [ entityType in EntityType ]: {
        type: entityType;
        entity: EntityTypeMap[entityType];
    }
};

export type PendingMediaStoreState = {
    [pendingMediaId: string]: PendingMediaObject;
};

const entityDetailsState = {
    originalEntity: null as {
        type: string,
        entity: unknown;
    } | null,
    // must be unknown or else type discrimination becomes too complex
    entity: null as { type: string, entity: unknown; pendingMedia: PendingMediaStoreState } | null,
};

const mutations = mutationTree(entityDetailsState, {
    initializeMaintenance (state, payload: { type: string, entity: unknown, originalEntity?: unknown }) {
        // release any potential file preview URLs
        for (const pendingMedia of Object.values(state.entity?.pendingMedia || {})) {
            URL.revokeObjectURL((pendingMedia as PendingMediaStoreState[string]).preview);
        }
        
        state.originalEntity = {
            type: payload.type,
            entity: payload.originalEntity || _cloneDeep(payload.entity),
        };
        state.entity = {
            type: payload.type,
            entity: payload.entity,
            pendingMedia: {},
        };
    },
    setPageEntity<T extends EntityType> (state: typeof entityDetailsState, entity: { type: T, entity: EntityTypeMap[T], pendingMedia?: PendingMediaStoreState }): void {
        state.entity = {
            type: entity.type,
            entity: entity.entity,
            pendingMedia: entity.pendingMedia || state.entity?.pendingMedia || {},
        };
    },
    clearEntity (state): void {
        state.entity = null;
    },
    setPendingMedia (state, payload: { id: string, media: PendingMediaObject }): void {
        if (!state.entity) {
            return;
        }
        state.entity = {
            ...state.entity,
            pendingMedia: {
                ...state.entity.pendingMedia,
                [payload.id]: payload.media,
            },
        };
    },
    removePendingMedia (state, payload: string): void {
        if (!state.entity) {
            return;
        }
        if (state.entity.pendingMedia[payload]) {
            URL.revokeObjectURL(state.entity.pendingMedia[payload].preview);
            delete state.entity.pendingMedia[payload];
        }
    },
    clearPendingMedia (state): void {
        if (!state.entity) {
            return;
        }
        for (const pendingMedia of Object.values(state.entity.pendingMedia)) {
            URL.revokeObjectURL(pendingMedia.preview);
        }
        state.entity.pendingMedia = {};
    },
    addPendingMedia (state, payload: PendingMediaObject): void {
        if (!state.entity) {
            return;
        }
        state.entity.pendingMedia[payload.id] = payload;
    },
});

const getters = getterTree(entityDetailsState, {
    entityAsType: state => <T extends EntityType>(type: T): EntityTypeMap[T] | null => {
        if (state.entity?.type === type) {
            return state.entity.entity as EntityTypeMap[T];
        }
        return null;
    },
    originalEntityAsType: state => <T extends EntityType>(type: T): EntityTypeMap[T] | null => {
        if (state.originalEntity?.type === type) {
            return state.originalEntity.entity as EntityTypeMap[T];
        }
        return null;
    },
});

export default {
    state: entityDetailsState,
    mutations,
    getters,
};
